// Which membership a check-in spends, mirroring MembershipEntryConsumptionRule on the server.
//
// Reception picks; the server still decides. Sending an id it judges unspendable makes it fall back
// to its own choice rather than fail, so the two can never disagree about what was deducted -- but
// they must agree about what is *offered*, or the operator is choosing from a list that does not
// match what the deduction will accept.

const startOfDay = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const remaining = (membership) => (membership.numberOfEntries || 0)
  - (membership.entriesCounter || 0);

// Membership ids are the 18-digit composite, which is past what a JS number holds exactly: two
// consecutive ids subtract to 0 and the tie-break silently stops breaking ties. Compared as the
// strings they arrive as, shorter first so the order still reads numerically.
const byId = (a, b) => {
  const left = String(a === undefined || a === null ? '' : a);
  const right = String(b === undefined || b === null ? '' : b);

  return left.length - right.length || left.localeCompare(right);
};

// Only the stored status is read. statusData.code derives "expired" from the end date and the
// entry count, which the date and entry checks below already cover -- reading both would reject a
// membership twice for the same reason and hide why.
const isLive = (membership) => String(membership.statusCode || '').toLowerCase() === 'active';

// In force for a visit at this moment, entries aside.
const coversVisit = (membership, at) => {
  if (!membership || !isLive(membership)) return false;

  const today = startOfDay(at);
  const starts = startOfDay(membership.startingDate);
  const ends = startOfDay(membership.endDate);

  if (starts === null || starts > today) return false;
  return ends !== null && ends >= today;
};

// Boxing and the like are sold on top of a gym membership, never instead of one.
export const isMain = (membership) => {
  const type = membership && membership.membershipTypeData;
  return !type || Number(type.canBeAdditionalMembership) !== 1;
};

export const isSpendable = (membership, at = new Date()) => {
  if (!membership || !membership.isEntryBased) return false;
  if (remaining(membership) <= 0) return false;

  return coversVisit(membership, at);
};

// A main membership in force that takes no entry for a visit -- the open-ended gym plan whose whole
// job here is to keep a gym visit off the member's boxing entries.
const carriesFreely = (membership, at) => isMain(membership)
  && coversVisit(membership, at)
  && !membership.isEntryBased;

// A used-up gym plan is not one the member is still using, so it stops shielding the boxing
// entries.
const carries = (membership, at) => carriesFreely(membership, at)
  || (isMain(membership) && isSpendable(membership, at));

// Soonest to expire first, so entries already paid for are not stranded behind a longer plan.
// Fewest left, then lowest id, keep the order stable between two renders.
const byPriority = (a, b) => startOfDay(a.endDate) - startOfDay(b.endDate)
  || remaining(a) - remaining(b)
  || byId(a.id, b.id);

export const spendableMemberships = (memberships, at = new Date()) => (memberships || [])
  .filter((membership) => isSpendable(membership, at))
  .sort(byPriority);

// Everything reception may put the visit against: the memberships that can answer for it at all,
// which includes an open-ended gym plan that will take nothing. Main ones first, because that is
// what the gate would do.
export const selectableMemberships = (memberships, at = new Date()) => {
  const all = memberships || [];
  const carrying = all.filter((membership) => carries(membership, at));
  const additional = all.filter((membership) => !isMain(membership) && isSpendable(membership, at));

  return [...carrying.sort(byPriority), ...additional.sort(byPriority)];
};

// What the server would spend if reception said nothing. Mirrors MembershipEntryConsumptionRule:
// a main membership answers for the visit while it can, and one that answers for free answers
// first.
export const defaultMembershipToSpend = (memberships, at = new Date()) => {
  const all = memberships || [];
  const carrying = all.filter((membership) => carries(membership, at));

  if (carrying.length > 0) {
    const [free] = carrying.filter((membership) => carriesFreely(membership, at)).sort(byPriority);
    if (free) return free;

    const [best] = carrying.sort(byPriority);
    return best || null;
  }

  const [fallback] = spendableMemberships(all.filter((m) => !isMain(m)), at);
  return fallback || null;
};

// Reception is choosing between plans that may share a name, so an option has to carry what tells
// them apart: how many entries are left on it and when it runs out. An open-ended plan says so
// instead of a count, because picking it deducts nothing.
export const membershipPickerOptions = (
  memberships,
  {
    entriesLeftLabel, noEntriesLabel, formatDate, at,
  },
) => (
  selectableMemberships(memberships, at || new Date()).map((membership) => {
    const name = (membership.membershipTypeData && membership.membershipTypeData.name) || '';
    const left = membership.isEntryBased
      ? entriesLeftLabel(remaining(membership))
      : noEntriesLabel();
    const ends = membership.endDate ? ` — ${formatDate(membership.endDate)}` : '';

    return { value: membership.id, text: `${name} (${left}${ends})` };
  })
);

export const remainingEntries = remaining;
